import { useState, type FormEvent } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, Navigate, useLocation, useNavigate } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { SsoButtons } from "@/components/SsoButtons";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { useAuthStore } from "@/store/authStore";

export function LoginPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, status, error, isAuthenticated } = useAuthStore();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  if (isAuthenticated()) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const isBusy = status === "authenticating";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await signIn(
        email.trim().toLowerCase(),
        password,
        mfaCode ? mfaCode.trim() : undefined,
      );
      setMfaRequired(false);
      setMfaCode("");
      const from = (location.state as { from?: string } | null)?.from ?? "/";
      navigate(from, { replace: true });
    } catch (err) {
      const resp = (err as { response?: { data?: { detail?: string }; headers?: Record<string, string>; status?: number } }).response;
      const detail = resp?.data?.detail;
      if (detail === "mfa_required" || detail === "invalid_mfa_code") {
        setMfaRequired(true);
      }
      if (resp?.status === 429) {
        const ra = resp.headers?.["retry-after"];
        setRetryAfter(ra ? Number(ra) : null);
      } else {
        setRetryAfter(null);
      }
    }
  };

  const bg = theme.palette.mode === "dark" ? "/brand/bg-dark.svg" : "/brand/bg-light.svg";

  const errorMessage =
    error === "invalid_credentials"
      ? t("auth.loginFailed")
      : error === "user_inactive"
        ? t("auth.userInactive")
        : error === "mfa_required"
          ? t("auth.mfaRequired")
          : error === "invalid_mfa_code"
            ? t("auth.invalidMfaCode")
            : error === "too_many_attempts"
              ? retryAfter
                ? t("auth.tooManyAttemptsWithDelay", { seconds: retryAfter })
                : t("auth.tooManyAttempts")
              : error;

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        px: 2,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: "absolute", top: 16, right: 16 }}
      >
        <LocaleSwitcher />
        <ThemeSwitcher />
      </Stack>

      <Card
        sx={{
          width: "100%",
          maxWidth: 440,
          backdropFilter: "blur(8px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(30, 30, 30, 0.85)"
              : "rgba(255, 255, 255, 0.92)",
          borderColor: "divider",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={3}>
            <Stack alignItems="center" spacing={1.5}>
              <BrandMark variant="wordmark" height={56} />
              <Typography variant="h5" sx={{ fontWeight: 700, mt: 1 }}>
                {t("auth.loginTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("auth.loginSubtitle")}
              </Typography>
            </Stack>

            {status === "error" && errorMessage && (
              <Alert severity="error">{errorMessage}</Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField
                  label={t("auth.email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  fullWidth
                  disabled={isBusy}
                />
                <TextField
                  label={t("auth.password")}
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                  disabled={isBusy}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPwd((v) => !v)}
                            edge="end"
                            size="small"
                          >
                            {showPwd ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {mfaRequired && (
                  <TextField
                    label={t("auth.mfaCodeLabel")}
                    value={mfaCode}
                    onChange={(e) =>
                      setMfaCode(e.target.value.replace(/\s+/g, "").slice(0, 8))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    fullWidth
                    disabled={isBusy}
                    helperText={t("auth.mfaCodeHelp")}
                  />
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isBusy || !email || !password || (mfaRequired && !mfaCode)}
                  sx={{ py: 1.2 }}
                >
                  {isBusy ? t("common.loading") : t("auth.loginCta")}
                </Button>
              </Stack>
            </Box>

            <SsoButtons
              email={email}
              disabled={isBusy}
              nextPath={(location.state as { from?: string } | null)?.from}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              {t("auth.applyHint")}{" "}
              <Box
                component={RouterLink}
                to="/apply"
                sx={{ color: "primary.main", textDecoration: "none", fontWeight: 600 }}
              >
                {t("auth.applyLink")}
              </Box>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
